import { useEffect } from "react";
import { Link } from "react-router-dom";
import ContactForm from "@/pages/about/components/ContactForm";

const aboutHeroImage =
  "https://readdy.ai/api/search-image?query=abstract%20cinematic%20background%20of%20a%20dark%20blue%20theater%20with%20warm%20orange%20light%20beams%20and%20floating%20film%20strips%2C%20deep%20blue%20and%20warm%20orange%20tones%2C%20moody%20atmosphere%2C%20highly%20detailed%2C%20artistic&width=1600&height=700&seq=abouthero&orientation=landscape";

const stats = [
  { value: "12,000+", label: "影视条目" },
  { value: "2,600+", label: "电视剧全集" },
  { value: "每日", label: "资源更新" },
  { value: "9 大", label: "内容分类" },
];

const guides = [
  {
    icon: "ri-search-line",
    title: "如何快速找到想看的影视？",
    desc: "在顶部搜索框输入片名、类型或主演即可模糊搜索；也可以进入分类页，通过类型、地区、年份与热度多维筛选。",
  },
  {
    icon: "ri-layout-grid-line",
    title: "如何浏览最新与热门内容？",
    desc: "首页的“焦点区”“最新更新”“本周热播剧”会持续呈现最新上线与高口碑作品，点击卡片即可进入详情页。",
  },
  {
    icon: "ri-file-list-3-line",
    title: "电视剧怎么看分集？",
    desc: "在电视剧详情页的“剧集目录”中选择对应集数，即可查看该集信息（演示功能，点击可切换）。",
  },
  {
    icon: "ri-share-line",
    title: "如何分享影视作品？",
    desc: "详情页点击“分享”按钮可弹出分享面板，用于演示分享交互，暂未接入真实分享接口。",
  },
];

export default function About() {
  useEffect(() => {
    document.title = "关于我们 - 97影院";
  }, []);

  return (
    <div className="w-full">
      <section className="relative w-full overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={aboutHeroImage}
            alt="97影院 关于我们"
            title="关于 97影院"
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-primary-950/60 to-primary-950/50" />
        </div>
        <div className="relative z-10 mx-auto w-full max-w-[1440px] px-4 py-20 md:px-8 md:py-28">
          <nav className="mb-4 flex items-center gap-1.5 text-sm text-background-200">
            <Link to="/" className="transition-colors hover:text-accent-400">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-background-50">关于我们</span>
          </nav>
          <h1 className="max-w-3xl font-heading text-3xl font-black leading-tight text-background-50 md:text-5xl">
            关于 97影院
          </h1>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-background-200 md:text-base">
            97影院是一个综合影视资源浏览平台，专注于为影视爱好者提供便捷的分类、筛选与搜索体验，
            帮助你快速找到最新电影、热门电视剧全集与相关影视资讯。
          </p>
        </div>
      </section>

      <div className="mx-auto w-full max-w-[1440px] px-4 md:px-8">
        <section className="-mt-10 grid grid-cols-2 gap-4 md:-mt-14 md:grid-cols-4 md:gap-6">
          {stats.map((s) => (
            <div key={s.label} className="rounded-lg border border-background-200 bg-background-50 p-5 text-center">
              <p className="font-heading text-2xl font-black text-primary-700 md:text-3xl">{s.value}</p>
              <p className="mt-1.5 text-xs text-foreground-500 md:text-sm">{s.label}</p>
            </div>
          ))}
        </section>

        <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-14">
          <section>
            <h2 className="flex items-center gap-2 font-heading text-2xl font-bold text-foreground-950">
              <span className="h-6 w-1.5 rounded-full bg-accent-500" />
              网站定位
            </h2>
            <p className="mt-4 text-sm leading-7 text-foreground-600">
              97影院面向各年龄段的观影人群，尤其服务于追求最新影视资源与便捷浏览体验的用户。
              我们将海量影视条目按电影、电视剧两大方向组织，并提供类型、地区、年份、热度等多维度筛选，
              让“找到想看的作品”这件事变得简单高效。
            </p>
            <p className="mt-3 text-sm leading-7 text-foreground-600">
              平台内容以结构化信息呈现：每部作品都包含海报、评分、题材、地区、年份与剧情简介，
              电视剧还提供分集目录，方便用户按图索骥。
            </p>
          </section>

          <section>
            <h2 className="flex items-center gap-2 font-heading text-2xl font-bold text-foreground-950">
              <span className="h-6 w-1.5 rounded-full bg-accent-500" />
              发展愿景
            </h2>
            <p className="mt-4 text-sm leading-7 text-foreground-600">
              我们希望把 97影院打造为一个清晰、舒适、值得信赖的影视信息门户，让内容组织更专业、检索更智能、
              浏览更沉浸。未来将持续完善分类体系与推荐机制，让每一位用户都能在这里发现属于自己的好片。
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              {["内容清晰", "检索高效", "体验沉浸", "持续更新"].map((tag) => (
                <span key={tag} className="rounded-full bg-secondary-100 px-4 py-2 text-sm text-secondary-900">
                  {tag}
                </span>
              ))}
            </div>
          </section>
        </div>

        <section className="mt-14">
          <h2 className="flex items-center gap-2 font-heading text-2xl font-bold text-foreground-950">
            <span className="h-6 w-1.5 rounded-full bg-accent-500" />
            使用说明
          </h2>
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5">
            {guides.map((g) => (
              <div key={g.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                  <i className={`${g.icon} text-xl`} />
                </span>
                <h3 className="mt-4 text-base font-semibold text-foreground-950">{g.title}</h3>
                <p className="mt-2 text-sm leading-6 text-foreground-500">{g.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14">
          <h2 className="flex items-center gap-2 font-heading text-2xl font-bold text-foreground-950">
            <span className="h-6 w-1.5 rounded-full bg-accent-500" />
            免责声明
          </h2>
          <div className="mt-4 rounded-lg border border-accent-200 bg-accent-50 p-5 md:p-6">
            <ul className="space-y-3 text-sm leading-6 text-accent-900">
              <li className="flex gap-2">
                <i className="ri-error-warning-line mt-0.5 shrink-0" />
                97影院为演示站点，页面中所有影视名称、海报、简介、演职人员等信息均为虚构示例内容，不指向任何真实作品或人物。
              </li>
              <li className="flex gap-2">
                <i className="ri-error-warning-line mt-0.5 shrink-0" />
                本站不提供任何真实影片的播放、下载或在线观看服务，播放、分享等相关按钮均为界面演示。
              </li>
              <li className="flex gap-2">
                <i className="ri-error-warning-line mt-0.5 shrink-0" />
                搜索、筛选、分页等交互为前端模拟演示，未接入真实后台数据接口。
              </li>
            </ul>
          </div>
        </section>

        <section className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-14">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-2xl font-bold text-foreground-950">
              <span className="h-6 w-1.5 rounded-full bg-accent-500" />
              联系我们
            </h2>
            <p className="mt-4 text-sm leading-7 text-foreground-600">
              如果你对 97影院有任何建议、内容纠错或合作意向，欢迎通过右侧表单与我们联系。
              我们重视每一位用户的声音，会认真阅读并持续改进。
            </p>
            <p className="mt-3 text-sm leading-7 text-foreground-500">
              说明：本站为演示项目，暂未提供真实客服联系方式，提交的信息仅用于功能演示。
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <span className="flex items-center gap-2 rounded-full border border-background-200 bg-background-50 px-4 py-2 text-sm text-foreground-600">
                <i className="ri-mail-line text-secondary-500" />
                演示邮箱：service@example.com
              </span>
              <span className="flex items-center gap-2 rounded-full border border-background-200 bg-background-50 px-4 py-2 text-sm text-foreground-600">
                <i className="ri-time-line text-secondary-500" />
                服务时间：工作日 10:00 - 18:00（演示）
              </span>
            </div>
          </div>
          <ContactForm />
        </section>

        <section className="mt-14 mb-4">
          <h2 className="flex items-center gap-2 font-heading text-2xl font-bold text-foreground-950">
            <span className="h-6 w-1.5 rounded-full bg-accent-500" />
            版权信息
          </h2>
          <p className="mt-4 text-sm leading-7 text-foreground-600">
            © 2026 97影院。本站所有内容仅用于产品演示与学习交流，不构成任何商业用途。
            未经许可，请勿将本站内容用于其他用途。如涉及版权问题，请通过上方表单与我们联系。
          </p>
        </section>
      </div>
    </div>
  );
}